import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useDisclosure } from '@mantine/hooks';
import { IconEye } from '@tabler/icons-react';
import Table from '@/components/Table';
import PageTemplate from '@/components/PageTemplate';
import agentMutations from '@/api/mutitations/agent';
import prepareTranslate from '@/helpers/dictionary';
import { shortenText, stripHtmlTags } from '@/helpers/string';
import { extractFilterOptions, filterAndSortData } from '@/components/Table/Table.functions';
import ViewAgentEmail from '@/components/ViewAgentEmails/ViewAgentEmail';

/**
 * Emails that were deleted from the agent's email log. Deleting an email only
 * removes the record from Equity, so the archive keeps it available to view.
 */
const ArchivedEmailsAgent = () => {
  const { id } = useParams();
  const t = prepareTranslate();

  const [filterOptions, setFilterOptions] = useState({});
  const [columnFilters, setColumnFilters] = useState({});
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [viewOpened, { open: openView, close: closeView }] = useDisclosure();

  const { mutateAsync: getAgent } = agentMutations.useGetOne();

  const columns = [
    {
      title: t('components.viewAgentEmails.columns.name'),
      accessor: 'name',
      isVisible: true,
      isFilterActive: true,
      sortable: true,
    },
    {
      title: t('components.viewAgentEmails.columns.date'),
      accessor: 'date',
      isVisible: true,
      isFilterActive: true,
      sortable: true,
      filterType: 'datePicker',
      transforms: [{ func: 'date', params: { withTime: false } }],
    },
    {
      title: t('components.viewAgentEmails.archive.deletedAt'),
      accessor: 'deletedAt',
      isVisible: true,
      isFilterActive: true,
      sortable: true,
      filterType: 'datePicker',
      transforms: [{ func: 'date', params: { withTime: false } }],
    },
    {
      title: t('components.viewAgentEmails.delete.reason'),
      accessor: 'deletionReason',
      isVisible: true,
      isFilterActive: true,
      sortable: true,
    },
    {
      title: t('components.viewAgentEmails.columns.content'),
      accessor: 'cleanContent',
      isVisible: true,
      isFilterActive: true,
      sortable: true,
    },
    {
      title: t('components.viewAgentEmails.columns.actions'),
      menuLabel: t('components.viewAgentEmails.columns.actions'),
      actions: [
        {
          title: t('components.viewAgentEmails.actions.view'),
          icon: <IconEye />,
          isPrimary: false,
          action: (_, row) => {
            setSelectedEmail(row);
            openView();
          },
        },
      ],
    },
  ];

  const getData = async (start, end, query, sort) => {
    try {
      const agent = await getAgent({ id });
      const deletedEmails = (agent?.emails || [])
        .filter((email) => email.status === 'deleted')
        .map((email) => ({
          ...email,
          cleanContent: shortenText(stripHtmlTags(email.content), 50),
        }));

      const filteredAndSorted = filterAndSortData(deletedEmails, query, sort);
      setFilterOptions(extractFilterOptions(filteredAndSorted, columns));

      return { data: filteredAndSorted, count: filteredAndSorted.length };
    } catch (error) {
      notifications.show({
        title: t('pages.viewAgentArchivedEmails.notification.errors.find.title'),
        message: t('pages.viewAgentArchivedEmails.notification.errors.find.message'),
      });
      return { data: [], count: 0 };
    }
  };

  return (
    <PageTemplate
      title={t('pages.viewAgentArchivedEmails.title')}
      withBackButton
    >
      <Table
        isExportActive={false}
        columns={columns}
        getData={getData}
        isColumnsActive={false}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        filterOptions={filterOptions}
      />

      <ViewAgentEmail
        open={openView}
        close={closeView}
        opened={viewOpened}
        email={selectedEmail}
      />
    </PageTemplate>
  );
};

export default ArchivedEmailsAgent;
